// * Package Imports
import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import InputChipComponent from "../../components/InputChipComponent/InputChipComponent";
import { faLinkedin, faReddit } from "@fortawesome/free-brands-svg-icons";
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { environment } from "../../environments/environment";
import { useAuthContext } from "../../hooks/useAuthContext";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import PostModal from "../../components/PostModal/PostModal";
import { stripHtmlToText } from "../../utils/postBodyUtils";
import "./JobDetail.css";
import "../ExecutionDetail/ExecutionDetail.css";

function JobDetails() {
  document.title = `Scout details | LeadZLab`;

  const [job, setJob] = useState({
    _id: "",
    jobName: "",
    timeFilter: "",
    postCount: 0,
  });
  const [leads, setLeads] = useState({ job: null });
  const [leadsTodayYesterday, setLeadsTodayYesterday] = useState([]);
  const [last7Posts, setLast7Posts] = useState([]);
  const [last7HasMore, setLast7HasMore] = useState(false);
  const [last7Loading, setLast7Loading] = useState(false);
  const [leadsSegment, setLeadsSegment] = useState("all");
  const [leadsLoader, setLeadsLoader] = useState(false);
  const [selectedPosts, setSelectedPosts] = useState([]);

  const [linkedInQuery, setLinkedInQuery] = useState("");
  const [objective, setObjective] = useState("");
  const [negativePrompt, setNegativePrompt] = useState("");
  const [subReddits, setSubreddits] = useState([]);
  const [redditHiringIntentGate, setRedditHiringIntentGate] = useState(false);
  const [redditHiringIntentDesignerPatterns, setRedditHiringIntentDesignerPatterns] = useState(false);
  const [jobType, setJobType] = useState("linkedin");
  const [profileAiLoading, setProfileAiLoading] = useState(false);
  const [queryPrompt, setQueryPrompt] = useState("");
  const [querySuggestLoading, setQuerySuggestLoading] = useState(false);
  const linkedInQueryInputRef = useRef(null);
  const linkedInQueryOverlayRef = useRef(null);
  const [jobDetailSidebarOpen, setJobDetailSidebarOpen] = useState(false);
  const [expandedPostId, setExpandedPostId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expandedReasonPostId, setExpandedReasonPostId] = useState(null);

  const location = useLocation();

  // * Auth context
  const { user } = useAuthContext();

  const handleJobNameChange = (e) => setJob((prev) => ({ ...prev, jobName: e.target.value }));

  const fillQueryWithAi = async () => {
    const prompt = (queryPrompt || "").trim();
    if (!prompt) return;
    setQuerySuggestLoading(true);
    try {
      const { data: res } = await axios.post(
        `${environment.BACKEND}/user/suggest-linkedin-queries`,
        { prompt },
        { headers: { Authorization: `Bearer ${user?.accessToken}` } }
      );
      const query = res?.data?.query;
      if (query) setLinkedInQuery(query);
    } catch (err) {
      const msg = err?.response?.data?.message ?? err.message;
      toast.error(msg);
    } finally {
      setQuerySuggestLoading(false);
    }
  };

  const fillWithProfileAi = async () => {
    setProfileAiLoading(true);
    try {
      const { data: res } = await axios.post(
        `${environment.BACKEND}/user/onboarding/generate-scout-from-profile`,
        { channel: jobType },
        { headers: { Authorization: `Bearer ${user?.accessToken}` } }
      );
      const d = res?.data || res;
      if (d?.jobName) setJob((prev) => ({ ...prev, jobName: d.jobName }));
      if (d?.objective != null) setObjective(d.objective || "");
      if (d?.negativePrompt != null) setNegativePrompt(d.negativePrompt || "");
      if (jobType === "reddit") {
        if (Array.isArray(d?.subreddits) && d.subreddits.length) setSubreddits(d.subreddits);
      }
      toast.success("Scout details filled from your profile. Review and save.");
    } catch (err) {
      toast.error(err?.response?.data?.message ?? "Could not generate from profile");
    } finally {
      setProfileAiLoading(false);
    }
  };

  function parseSubredditsFromQuery(redditQuery) {
    const subredditsRegex = /subreddit:(\w+)/g;
    const subreddits = [];
    let match;
    while ((match = subredditsRegex.exec(redditQuery)) !== null) {
      subreddits.push(match[1]);
    }
    return subreddits;
  }

  const getJobById = async () => {
    try {
      const searchParams = new URLSearchParams(location.search);
      const jobId = searchParams.get("job_id");
      let { data } = await axios.get(
        `${environment.BACKEND}/resources/job/${jobId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.accessToken}`,
          },
        }
      );

      const j = data.data;
      setJobType(j.type);
      setJob(j);
      setObjective(j.objective || "");
      setNegativePrompt(j.negativePrompt || "");
      if (j.type === "reddit") {
        setSubreddits(parseSubredditsFromQuery(j.q || ""));
        setRedditHiringIntentGate(!!j.redditHiringIntentGate);
        setRedditHiringIntentDesignerPatterns(!!j.redditHiringIntentDesignerPatterns);
      } else {
        const q = (j.q || "").trim();
        setLinkedInQuery(q ? q.split(/\s+ or \s+/i).map((s) => s.trim()).filter(Boolean).join("\n") : "");
      }
    } catch (err) {
      console.log(err.response.data);
    }
  };

  useEffect(() => {
    getJobById();
  }, []);

  useEffect(() => {
    if (!jobDetailSidebarOpen) return;
    const onEscape = (e) => {
      if (e.key === "Escape") setJobDetailSidebarOpen(false);
    };
    document.addEventListener("keydown", onEscape);
    return () => document.removeEventListener("keydown", onEscape);
  }, [jobDetailSidebarOpen]);

  useEffect(() => {
    if (!isModalOpen) return;
    const onEscape = (e) => {
      if (e.key === "Escape") setIsModalOpen(false);
    };
    document.addEventListener("keydown", onEscape);
    return () => document.removeEventListener("keydown", onEscape);
  }, [isModalOpen]);

  useEffect(() => {
    if (!isModalOpen) return;
    const scrollY = window.scrollY ?? window.pageYOffset;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    const prevStyle = {
      overflow: document.body.style.overflow,
      paddingRight: document.body.style.paddingRight,
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
    };
    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = scrollbarWidth > 0 ? `${scrollbarWidth}px` : "";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    return () => {
      Object.assign(document.body.style, prevStyle);
      window.scrollTo(0, scrollY);
    };
  }, [isModalOpen]);

  const PAGE_SIZE = 20;

  const getTodayYesterdayLeads = async () => {
    const searchParams = new URLSearchParams(location.search);
    const jobId = searchParams.get("job_id");
    if (!jobId || !user?.accessToken) return;
    setLeadsLoader(true);
    try {
      const { data } = await axios.get(
        `${environment.BACKEND}/resources/job/${jobId}/leads?segment=${leadsSegment}&bucket=today_yesterday`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.accessToken}`,
          },
        }
      );
      setLeads({ job: data.data.job });
      setLeadsTodayYesterday(data.data.posts || []);
    } catch (err) {
      // ignore
    } finally {
      setLeadsLoader(false);
    }
  };

  const getLast7Leads = async (reset = true) => {
    const searchParams = new URLSearchParams(location.search);
    const jobId = searchParams.get("job_id");
    if (!jobId || !user?.accessToken) return;
    const skip = reset ? 0 : last7Posts.length;
    if (reset) {
      setLast7Posts([]);
      setLast7HasMore(true);
    }
    setLast7Loading(true);
    try {
      const { data } = await axios.get(
        `${environment.BACKEND}/resources/job/${jobId}/leads?segment=${leadsSegment}&bucket=last7&skip=${skip}&limit=${PAGE_SIZE}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.accessToken}`,
          },
        }
      );
      setLeads((prev) => ({ ...prev, job: prev.job || data.data.job }));
      setLast7HasMore(data.data.hasMore ?? false);
      if (reset) {
        setLast7Posts(data.data.posts || []);
      } else {
        setLast7Posts((prev) => [...prev, ...(data.data.posts || [])]);
      }
    } catch (err) {
      setLast7HasMore(false);
    } finally {
      setLast7Loading(false);
    }
  };

  useEffect(() => {
    if (!job._id) return;
    getTodayYesterdayLeads();
    getLast7Leads(true);
  }, [job._id, leadsSegment]);

  const getRelativeTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(Date.parse(dateString));
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
    if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks !== 1 ? "s" : ""} ago`;
    return "";
  };

  const getIntentLevel = (post) => {
    if (post.relevanceScore != null && typeof post.relevanceScore === "number") {
      if (post.relevanceScore >= 70) return "high";
      if (post.relevanceScore >= 40) return "medium";
      return "low";
    }
    const up = typeof post.upvotes === "number" ? post.upvotes : parseInt(post.upvotes, 10) || 0;
    if (up >= 15) return "high";
    if (up >= 5) return "medium";
    return "low";
  };

  const getIntentLabel = (post, intent) => {
    if (post.relevanceScore != null && typeof post.relevanceScore === "number") {
      return `${post.relevanceScore}%`;
    }
    return intent === "high" ? "High intent" : intent === "medium" ? "Medium intent" : "Low intent";
  };

  const getIntentTooltip = (post) => {
    if (post.relevanceScore != null && typeof post.relevanceScore === "number") {
      const reason = post.relevanceReason ? ` — ${post.relevanceReason}` : "";
      return `Relevance: ${post.relevanceScore}/100${reason}`;
    }
    const up = typeof post.upvotes === "number" ? post.upvotes : parseInt(post.upvotes, 10) || 0;
    if (up >= 15) return `High intent: ${up} upvotes`;
    if (up >= 5) return `Medium intent: ${up} upvotes`;
    return `Low intent: ${up} upvote${up !== 1 ? "s" : ""}`;
  };

  const hasBudgetMentioned = (post) => /\$|\bbudget\b/.test((post.body || "").toLowerCase());
  const hasHiring = (post) => /\bhiring\b|\blooking for\b|\bneed (a|someone)\b/.test(((post.body || "") + " " + (post.postTitle || "")).toLowerCase());
  const getPostTags = (post) => {
    const tags = [];
    if (hasBudgetMentioned(post)) tags.push("Budget mentioned");
    if (hasHiring(post)) tags.push("Hiring");
    return tags;
  };

  const handleSelect = (e) => {
    const id = e.target.value;
    if (e.target.checked) {
      setSelectedPosts((prev) => [...prev, id]);
    } else {
      setSelectedPosts((prev) => prev.filter((x) => x !== id));
    }
  };

  const selectAll = () => {
    if (allDisplayedPosts.length === 0) return;
    if (selectedPosts.length >= allDisplayedPosts.length) {
      setSelectedPosts([]);
    } else {
      setSelectedPosts(allDisplayedPosts.map((p) => String(p._id)));
    }
  };

  const getPostDateKey = (post) => {
    const raw = post.post_created_at || post.createdAt;
    if (!raw) return "";
    const d = new Date(raw);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  // Today + Yesterday from API; Last 7 days is loaded separately (paginated).
  const groupTodayYesterday = (posts) => {
    if (!posts || !posts.length) return [];
    const now = new Date();
    const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayKey = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, "0")}-${String(yesterday.getDate()).padStart(2, "0")}`;
    const today = [];
    const yesterdayArr = [];
    posts.forEach((post) => {
      const key = getPostDateKey(post);
      if (key === todayKey) today.push(post);
      else if (key === yesterdayKey) yesterdayArr.push(post);
    });
    const batches = [
      { key: "today", label: "Today", posts: today },
      { key: "yesterday", label: "Yesterday", posts: yesterdayArr },
    ];
    return batches.filter((b) => b.posts.length > 0);
  };

  const allDisplayedPosts = [...leadsTodayYesterday, ...last7Posts];

  const last7SentinelRef = useRef(null);

  useEffect(() => {
    const sentinel = last7SentinelRef.current;
    if (!sentinel || !last7HasMore || last7Loading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        getLast7Leads(false);
      },
      { rootMargin: "200px", threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [last7HasMore, last7Loading, last7Posts.length]);

  const updateReadStatus = async (isRead, postIdsOptional) => {
    const ids = postIdsOptional != null ? postIdsOptional : selectedPosts;
    if (!ids || ids.length === 0) return;
    try {
      await axios.put(
        `${environment.BACKEND}/resources/post/read-status`,
        { postIds: ids, is_read: isRead },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${user?.accessToken}` } }
      );
      const idSet = new Set(ids);
      setLeadsTodayYesterday((prev) => prev.map((p) => (idSet.has(String(p._id)) ? { ...p, is_read: isRead } : p)));
      setLast7Posts((prev) => prev.map((p) => (idSet.has(String(p._id)) ? { ...p, is_read: isRead } : p)));
      if (postIdsOptional == null) setSelectedPosts([]);
    } catch (err) {
      toast.error(err?.response?.data?.message ?? "Failed to update");
    }
  };

  const togglePostExpansion = (postId) => {
    updateReadStatus(true, [postId]);
    if (isModalOpen && String(expandedPostId) === String(postId)) {
      setIsModalOpen(false);
    } else {
      setIsModalOpen(true);
      setExpandedPostId(postId);
    }
  };

  const handleUpdateJob = async () => {
    try {
      if (jobType === "linkedin") {
        const raw = (linkedInQuery || "").trim();
        if (!raw) throw new Error("Please enter a search query for LinkedIn");
        const q = raw.split(/\s*\n+\s*|\s+ or \s+/i).map((s) => s.trim()).filter(Boolean).join(" or ");
        if (!q) throw new Error("Please enter at least one search phrase.");
        if (q.length > 500) throw new Error("Search query must be 500 characters or less.");
        await axios.patch(
          `${environment.BACKEND}/resources/job/${job._id}`,
          {
            jobName: job.jobName,
            q,
            objective: (objective || "").trim(),
            negativePrompt: (negativePrompt || "").trim(),
            timeFilter: "day",
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.accessToken}`,
            },
          }
        );
      } else {
        if (subReddits.length === 0)
          throw new Error("Please add at least one subreddit. We'll validate that each exists.");

        const normalizedSubreddits = subReddits
          .map((s) => (s.startsWith("r/") ? s.slice(2) : s).trim().toLowerCase())
          .filter(Boolean);

        await axios.patch(
          `${environment.BACKEND}/resources/job/${job._id}`,
          {
            jobName: job.jobName,
            subreddits: normalizedSubreddits,
            objective: (objective || "").trim(),
            negativePrompt: (negativePrompt || "").trim(),
            timeFilter: "day",
            redditHiringIntentGate,
            redditHiringIntentDesignerPatterns,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.accessToken}`,
            },
          }
        );
      }

      toast.success(`Job updated successfully`, {
        position: "top-right",
        autoClose: true,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: 0,
      });
    } catch (err) {
      toast.error(`${err?.response?.data?.message ?? err.message}`, {
        position: "top-right",
        autoClose: true,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: 0,
      });
    }
  };

  const renderLeadRow = (post) => {
    const isUnread = !post.is_read;
    const intent = getIntentLevel(post);
    const intentBarClass = intent === "high" ? "execution-row-bar-high" : intent === "medium" ? "execution-row-bar-medium" : "execution-row-bar-low";
    const link = post.postLink || post.linkedInProfileURL || "#";
    const isLinkedIn = leads.job?.type === "linkedin";
    const displayName = isLinkedIn ? (post.user || "—") : (post.postTitle || post.user || "Lead");
    const subLabel = !isLinkedIn && post.subreddit_name ? (post.subreddit_name.startsWith("r/") ? post.subreddit_name : "r/" + post.subreddit_name) : null;
    const bodyPreview = isLinkedIn ? stripHtmlToText(post.body || "").trim() : "";
    const bodySnippet = bodyPreview.slice(0, 180);
    return (
      <tr
        key={post._id}
        className={[
          "execution-row",
          isUnread ? "execution-row-unread" : "execution-row-read",
          selectedPosts.includes(String(post._id)) ? "execution-row-selected" : "",
          intentBarClass,
        ].filter(Boolean).join(" ")}
        onClick={() => togglePostExpansion(post._id)}
        onKeyDown={(e) => e.key === "Enter" && togglePostExpansion(post._id)}
        role="button"
        tabIndex={0}
        aria-label={isLinkedIn ? `View ${post.user}` : `View ${displayName}`}
      >
        <td className="execution-cell-checkbox" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
            onChange={handleSelect}
            checked={selectedPosts.includes(String(post._id))}
            value={String(post._id)}
            aria-label={isLinkedIn ? `Select ${post.user}` : `Select ${displayName}`}
          />
        </td>
        <td className="execution-cell-title">
          <div className="execution-row-title-block">
            <span className="execution-row-dot-slot">
              {isUnread ? <span className="execution-row-dot" aria-hidden /> : null}
            </span>
            <div className="execution-row-title-content">
              <div className="execution-row-title-line">
                {isLinkedIn ? (
                  <a
                    href={post.linkedInProfileURL || link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`execution-row-title-text no-underline ${isUnread ? "font-semibold text-slate-900 hover:text-emerald-600" : "font-medium text-slate-700 hover:text-emerald-600"}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {post.user || "—"}
                  </a>
                ) : (
                  <span className={`execution-row-title-text ${isUnread ? "font-semibold text-slate-900" : "font-medium text-slate-700"}`} title={displayName}>
                    {displayName}
                  </span>
                )}
                {isUnread && <span className="execution-pill-new">NEW</span>}
              </div>
              <p className="execution-row-meta">
                {isLinkedIn ? (
                  <>
                    {post.post_created_at ? (
                      <>
                        {new Date(Date.parse(post.post_created_at)).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true })}
                        {getRelativeTime(post.post_created_at) && (
                          <>
                            <span className="execution-meta-sep">·</span>
                            <span className="execution-meta-ago">{getRelativeTime(post.post_created_at)}</span>
                          </>
                        )}
                      </>
                    ) : "—"}
                  </>
                ) : (
                  <>
                    {subLabel && (
                      <>
                        <a
                          href={`https://www.reddit.com/${subLabel}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-emerald-600 hover:text-emerald-700 no-underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {subLabel}
                        </a>
                        <span className="execution-meta-sep">·</span>
                      </>
                    )}
                    <span>
                      {post.post_created_at && new Date(Date.parse(post.post_created_at)).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true })}
                      {getRelativeTime(post.post_created_at) && (
                        <>
                          <span className="execution-meta-sep">·</span>
                          <span className="execution-meta-ago">{getRelativeTime(post.post_created_at)}</span>
                        </>
                      )}
                    </span>
                    {post.upvotes != null && (
                      <>
                        <span className="execution-meta-sep">·</span>
                        <span>{post.upvotes ?? 0} upvotes</span>
                      </>
                    )}
                  </>
                )}
              </p>
              {!isLinkedIn && (
                <div className="execution-row-tags">
                  {getPostTags(post).map((tag) => (
                    <span key={tag} className="execution-pill-tag execution-tooltip-trigger" title={tag === "Budget mentioned" ? "Post mentions budget or $ amount" : "Post mentions hiring or looking for help"}>
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </td>
        {leads.job?.type === "linkedin" && (
          <td className="execution-cell-preview">
            <p className="text-sm text-slate-600 line-clamp-2" title={bodyPreview || undefined}>
              {bodySnippet ? (bodySnippet + (bodyPreview.length > bodySnippet.length ? "…" : "")) : "—"}
            </p>
          </td>
        )}
        <td className="execution-cell-intent" onClick={(e) => e.stopPropagation()}>
          <div className="flex flex-col gap-0.5">
            {post.relevanceReason && (post.relevanceScore != null || leads.job?.type === "linkedin") ? (
              <>
                <button
                  type="button"
                  className={`execution-pill-intent intent-${intent} execution-pill-intent-clickable text-left cursor-pointer`}
                  onClick={() => setExpandedReasonPostId((prev) => (prev === post._id ? null : post._id))}
                  title="Click to show why AI marked this lead"
                >
                  {getIntentLabel(post, intent)}
                </button>
                {expandedReasonPostId === post._id && (
                  <p className="execution-row-llm-reason text-xs text-slate-600 mt-0.5 pr-1 max-w-[240px]">
                    {post.relevanceReason}
                  </p>
                )}
              </>
            ) : (
              <span className={`execution-pill-intent intent-${intent}`} title={getIntentTooltip(post)}>
                {getIntentLabel(post, intent)}
              </span>
            )}
          </div>
        </td>
                      <td className="execution-cell-action" onClick={(e) => e.stopPropagation()}>
                        <div className="execution-row-actions">
                          <a href={link} target="_blank" rel="noopener noreferrer" className="execution-btn-view" onClick={(e) => e.stopPropagation()}>
                            View
                          </a>
                        </div>
                      </td>
      </tr>
    );
  };

  return (
    <div className="job-detail job-detail-tight mx-auto max-w-6xl py-2">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <Link
            to="/jobs"
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 shrink-0"
          >
            <ChevronLeftIcon className="h-4 w-4" /> Back to scouts
          </Link>
          <button
            type="button"
            onClick={() => setJobDetailSidebarOpen(true)}
            className="job-detail-scout-name-btn inline-flex items-center gap-1.5 text-left min-w-0"
            title="View scout details"
          >
            <span className="font-semibold text-slate-900 truncate">{job.jobName || "Scout"}</span>
            <ChevronRightIcon className="h-4 w-4 text-slate-400 shrink-0" aria-hidden />
          </button>
        </div>
        <span className="flex items-center gap-1.5 text-sm text-slate-500 shrink-0">
          {jobType === "reddit" && <FontAwesomeIcon icon={faReddit} className="text-[#ff4500]" />}
          {jobType === "linkedin" && <FontAwesomeIcon icon={faLinkedin} className="text-[#0077b5]" />}
          <span className="capitalize">{jobType}</span>
        </span>
      </div>

      {/* Job detail sidebar — scout details + edit */}
      {jobDetailSidebarOpen && (
        <>
          <div
            className="job-detail-sidebar-overlay"
            onClick={() => setJobDetailSidebarOpen(false)}
            onKeyDown={(e) => e.key === "Escape" && setJobDetailSidebarOpen(false)}
            role="button"
            tabIndex={0}
            aria-label="Close scout details"
          />
          <aside className="job-detail-sidebar" aria-label="Scout details">
            <div className="job-detail-sidebar-header">
              <h2 className="job-detail-sidebar-title">Scout details</h2>
              <button
                type="button"
                onClick={() => setJobDetailSidebarOpen(false)}
                className="job-detail-sidebar-close"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="job-detail-sidebar-body job-detail-sidebar-body-editable">
              <div className="job-detail-sidebar-field">
                <label className="job-detail-sidebar-label" htmlFor="sidebar-jobName">Name</label>
                <input
                  id="sidebar-jobName"
                  type="text"
                  value={job.jobName || ""}
                  onChange={handleJobNameChange}
                  className="input-base w-full mt-0.5"
                />
              </div>
              <div className="job-detail-sidebar-field">
                <span className="job-detail-sidebar-label">Type</span>
                <span className="job-detail-sidebar-value flex items-center gap-1.5 mt-0.5">
                  {jobType === "reddit" && <FontAwesomeIcon icon={faReddit} className="text-[#ff4500]" />}
                  {jobType === "linkedin" && <FontAwesomeIcon icon={faLinkedin} className="text-[#0077b5]" />}
                  {jobType ? String(jobType).charAt(0).toUpperCase() + jobType.slice(1) : "—"}
                </span>
              </div>
              {jobType === "linkedin" && (
                <>
                  <div className="job-detail-sidebar-field">
                    <label className="job-detail-sidebar-label" htmlFor="sidebar-query">Search query</label>
                    <div className="job-detail-query-wrap mt-0.5">
                      <div
                        ref={linkedInQueryOverlayRef}
                        className="job-detail-query-overlay"
                        aria-hidden
                      >
                        {(linkedInQuery || "").split(/(\s+or\s+)/gi).map((part, i) =>
                          /\s+or\s+/i.test(part) ? (
                            <span key={i} className="job-detail-query-or">{part}</span>
                          ) : (
                            part
                          )
                        )}
                      </div>
                      <textarea
                        ref={linkedInQueryInputRef}
                        id="sidebar-query"
                        value={linkedInQuery || ""}
                        onChange={(e) => setLinkedInQuery(e.target.value)}
                        onScroll={() => {
                          if (linkedInQueryInputRef.current && linkedInQueryOverlayRef.current) {
                            linkedInQueryOverlayRef.current.scrollTop = linkedInQueryInputRef.current.scrollTop;
                            linkedInQueryOverlayRef.current.scrollLeft = linkedInQueryInputRef.current.scrollLeft;
                          }
                        }}
                        placeholder={'One phrase per line, or separate with " or ". e.g.\nlooking for alternatives to Gong\nswitching from Aircall'}
                        rows={5}
                        className="job-detail-query-input input-base w-full resize-y font-mono text-sm"
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">Phrases are searched on LinkedIn; separate with &quot; or &quot; or new lines (we combine with &quot; or &quot;).</p>
                    <div className="mt-2 flex flex-col gap-1.5">
                      <input
                        type="text"
                        value={queryPrompt}
                        onChange={(e) => setQueryPrompt(e.target.value)}
                        placeholder="Describe who you want to find (e.g. people looking for alternatives to Gong)"
                        className="input-base w-full text-sm"
                      />
                      <button
                        type="button"
                        onClick={fillQueryWithAi}
                        disabled={querySuggestLoading || !queryPrompt.trim()}
                        className="text-xs font-medium text-emerald-600 hover:text-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {querySuggestLoading ? "Generating…" : "Suggest search queries with AI"}
                      </button>
                    </div>
                  </div>
                </>
              )}
              {jobType === "reddit" && (
                <>
                  <div className="job-detail-sidebar-field">
                    <label className="job-detail-sidebar-label">Subreddits</label>
                    <InputChipComponent
                      keywords={subReddits}
                      setKeywords={setSubreddits}
                      placeholder="forhire, freelance"
                    />
                  </div>
                  <div className="job-detail-sidebar-field">
                    <label className="job-detail-sidebar-label">Reddit · advanced</label>
                    <label className="mt-1.5 flex cursor-pointer items-start gap-2">
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 rounded border-slate-300"
                        checked={redditHiringIntentGate}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setRedditHiringIntentGate(on);
                          if (!on) setRedditHiringIntentDesignerPatterns(false);
                        }}
                      />
                      <span className="text-sm text-slate-700">Require hiring-intent text gate (after LLM)</span>
                    </label>
                    {redditHiringIntentGate && (
                      <label className="mt-2 flex cursor-pointer items-start gap-2 pl-6">
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 rounded border-slate-300"
                          checked={redditHiringIntentDesignerPatterns}
                          onChange={(e) => setRedditHiringIntentDesignerPatterns(e.target.checked)}
                        />
                        <span className="text-sm text-slate-700">Graphic designer keyword patterns (legacy)</span>
                      </label>
                    )}
                  </div>
                </>
              )}
              <div className="job-detail-sidebar-field">
                <div className="flex items-center justify-between gap-1">
                  <label className="job-detail-sidebar-label" htmlFor="sidebar-objective">Objective</label>
                  <button
                    type="button"
                    onClick={fillWithProfileAi}
                    disabled={profileAiLoading}
                    className="text-xs font-medium text-slate-500 hover:text-slate-700 disabled:opacity-50"
                  >
                    {profileAiLoading ? "…" : "Fill with AI"}
                  </button>
                </div>
                <textarea
                  id="sidebar-objective"
                  value={objective || ""}
                  onChange={(e) => setObjective(e.target.value)}
                  placeholder="What kind of leads you want"
                  rows={5}
                  className="input-base w-full resize-y mt-0.5"
                />
              </div>
              <div className="job-detail-sidebar-field">
                <label className="job-detail-sidebar-label" htmlFor="sidebar-exclude">Exclude</label>
                <textarea
                  id="sidebar-exclude"
                  value={negativePrompt || ""}
                  onChange={(e) => setNegativePrompt(e.target.value)}
                  placeholder={jobType === "linkedin" ? "e.g. Remote only, unpaid" : "e.g. [For Hire], unpaid"}
                  rows={3}
                  className="input-base w-full resize-y mt-0.5"
                />
              </div>
              <div className="job-detail-sidebar-field">
                <span className="job-detail-sidebar-label">Leads</span>
                <span className="job-detail-sidebar-value mt-0.5 block">{job.postCount ?? 0}</span>
              </div>
            </div>
            <div className="job-detail-sidebar-footer flex flex-col gap-2">
              <button
                type="button"
                onClick={async () => { await handleUpdateJob(); setJobDetailSidebarOpen(false); }}
                className="job-detail-sidebar-edit-btn"
              >
                Update & run
              </button>
              <button
                type="button"
                onClick={async () => { await handleUpdateJob(); setJobDetailSidebarOpen(false); }}
                className="job-detail-sidebar-edit-btn border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              >
                Run again
              </button>
            </div>
          </aside>
        </>
      )}

      {/* Leads first — primary action is reaching out */}
      <div className="job-detail-leads-section">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <h1 className="text-lg font-semibold text-slate-900">Leads</h1>
          <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
            <button
              type="button"
              onClick={() => setLeadsSegment("new")}
              className={`px-3 py-1.5 text-sm font-medium rounded-md ${leadsSegment === "new" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
            >
              New
            </button>
            <button
              type="button"
              onClick={() => setLeadsSegment("all")}
              className={`px-3 py-1.5 text-sm font-medium rounded-md ${leadsSegment === "all" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
            >
              All
            </button>
          </div>
        </div>
        {leadsLoader ? (
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <p className="text-sm text-slate-500">Loading leads…</p>
          </div>
        ) : !leadsLoader && !last7Loading && allDisplayedPosts.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-5 text-center">
            <p className="text-sm text-slate-500">
              {leadsSegment === "new" ? "No new unread leads." : "No leads yet. The scout will add them as it runs."}
            </p>
          </div>
        ) : (
          <div className="job-detail-leads-batches">
            {selectedPosts.length > 0 && (
              <div className="execution-table-sticky-bar">
                <span className="text-sm font-medium text-slate-700">{selectedPosts.length} selected</span>
                <div className="flex items-center gap-2">
                  <button type="button" className="execution-table-bar-btn secondary" onClick={() => updateReadStatus(false)}>Mark unread</button>
                  <button type="button" className="execution-table-bar-btn primary" onClick={() => updateReadStatus(true)}>
                    <MarkEmailReadOutlinedIcon fontSize="small" />
                    Mark read
                  </button>
                </div>
              </div>
            )}
            {groupTodayYesterday(leadsTodayYesterday).map((batch) => (
              <section key={batch.key} className="job-detail-leads-batch">
                <h2 className="job-detail-leads-batch-title">{batch.label} ({batch.posts.length})</h2>
                <div className="execution-table-wrap comfortable job-detail-tight">
                  <table className="execution-table">
                    <thead>
                      <tr>
                        <th className="execution-th-checkbox" scope="col">
                          <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer" onClick={selectAll} aria-label="Select all" />
                        </th>
                        <th className="execution-th-title" scope="col">{leads.job?.type === "linkedin" ? "Posted by" : "Title"}</th>
                        {leads.job?.type === "linkedin" && <th className="execution-th-preview" scope="col">Preview</th>}
                        <th className="execution-th-intent" scope="col">Relevance</th>
                        <th className="execution-th-action" scope="col">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {batch.posts.map((post) => renderLeadRow(post))}
                    </tbody>
                  </table>
                </div>
              </section>
            ))}
            {(last7Posts.length > 0 || last7Loading || last7HasMore) && (
              <section className="job-detail-leads-batch">
                <h2 className="job-detail-leads-batch-title">Last 7 days ({last7Posts.length}{last7HasMore ? "+" : ""})</h2>
                <div className="execution-table-wrap comfortable job-detail-tight">
                  <table className="execution-table">
                    <thead>
                      <tr>
                        <th className="execution-th-checkbox" scope="col">
                          <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer" onClick={selectAll} aria-label="Select all" />
                        </th>
                        <th className="execution-th-title" scope="col">{leads.job?.type === "linkedin" ? "Posted by" : "Title"}</th>
                        {leads.job?.type === "linkedin" && <th className="execution-th-preview" scope="col">Preview</th>}
                        <th className="execution-th-intent" scope="col">Relevance</th>
                        <th className="execution-th-action" scope="col">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {last7Posts.map((post) => renderLeadRow(post))}
                    </tbody>
                  </table>
                </div>
                {last7Loading && (
                  <div className="py-4 text-center text-sm text-slate-500">Loading more…</div>
                )}
                {last7HasMore && !last7Loading && <div ref={last7SentinelRef} className="h-2" aria-hidden />}
              </section>
            )}
          </div>
        )}
      </div>

      {isModalOpen && (
        <PostModal
          post={allDisplayedPosts.find((p) => String(p._id) === String(expandedPostId)) ?? null}
          onClose={() => setIsModalOpen(false)}
          isLinkedIn={jobType === "linkedin"}
          showAiOutreach={true}
          jobObjective={objective}
          accessToken={user?.accessToken}
        />
      )}
    </div>
  );
}

export default JobDetails;
